import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Drawer } from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useGetData } from "../../hooks";
import { IAppointment } from "../../interface/appointmentInterfaces";
import { formatAge } from "../../utils/utils";
import AppointmentActions from "../Actions/AppointmentActions";
import AppointmentActionBtn from "../Btn/AppointmentActionBtn";
import { AppointmentStatus } from "../../Constants";

interface AllRemindersProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GroupedAppointments {
  [date: string]: IAppointment[];
}

const AllReminders: React.FC<AllRemindersProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetData<IAppointment[]>(
    EndPoints.GetReminderAppointments,
  );

  const groupedAppointments: GroupedAppointments = {};
  if (data?.Data) {
    data.Data.forEach((appointment: IAppointment) => {
      const dateKey = moment(appointment?.Date).format("DD MMM YYYY");
      if (!groupedAppointments[dateKey]) {
        groupedAppointments[dateKey] = [];
      }
      groupedAppointments[dateKey].push(appointment);
    });
  }

  const sortedDates = Object.keys(groupedAppointments).sort((a, b) => {
    return moment(a, "DD MMM YYYY").diff(moment(b, "DD MMM YYYY"));
  });

  return (
    <>
      <Drawer
        isOpen={isOpen}
        header={t("all_reminders")}
        customStyle="w-96 !px-4 !overflow-auto !gap-0"
        headerCustomStyle="!py-4"
        onClose={() => {
          onClose();
        }}
      >
        {sortedDates.map((date) => (
          <div key={date} className="mb-4">
            <div className="flex items-center gap-2">
              <h3 className="mb-2 text-nowrap text-xs font-semibold text-body-text">
                {t("at")} {date}
              </h3>
              <div className="w-full border-t border-[#A0B2CE]" />
            </div>

            <div className="flex flex-col gap-2">
              {groupedAppointments[date].map((appointment: IAppointment) => (
                <div
                  className="flex flex-col gap-2 rounded-lg border border-InputFieldColor bg-input-field p-3"
                  key={appointment.Id}
                >
                  <div className="flex items-center gap-1">
                    <div className="rounded-[4px] bg-primary-click px-[6px] py-0.5 text-xs font-bold text-primary">
                      {moment(appointment?.Date).format("DD MMM YYYY")}
                    </div>
                    <div className="rounded-[4px] bg-primary-click px-[6px] py-0.5 text-xs font-bold text-primary">
                      {moment(appointment?.Date).format("h:mm A")}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center gap-0.5">
                    <div className="text-sm font-bold">
                      {appointment?.Account?.FullName}
                    </div>
                    <div className="text-xs text-body-text">
                      {t(appointment?.Account?.Gender?.trim().toLowerCase())}
                      {appointment?.Account?.Birthday &&
                        `  •  ${formatAge(appointment?.Account?.Birthday)} ${t("years_old")}`}
                    </div>
                  </div>
                  {appointment?.Status === AppointmentStatus.Approved ||
                  !appointment?.HasAction ? (
                    <AppointmentActionBtn
                      action="view_details"
                      onClick={(event) => {
                        navigate(`/appointments/${appointment?.Id}`);
                      }}
                    />
                  ) : (
                    <div className="flex gap-2">
                      <AppointmentActions
                        action="reject"
                        appointmentId={appointment?.Id}
                      />
                      <AppointmentActions
                        action="confirm"
                        appointmentId={appointment?.Id}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(!data?.Data || data.Data.length === 0) && !isLoading && (
          <div className="py-8 text-center text-body-text">
            {t("no_reminders_found")}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default AllReminders;
